/**
 * Format a number as currency
 * @param amount - The amount to format
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null || isNaN(amount)) {
    return "0 Cr";
  }

  const absAmount = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  if (absAmount >= 1000000000) {
    const value = (absAmount / 1000000000);
    return `${sign}${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)} BCr`;
  } else if (absAmount >= 1000000) {
    const value = (absAmount / 1000000);
    return `${sign}${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)} MCr`;
  } else if (absAmount >= 10000) {
    const value = (absAmount / 1000);
    // Special case for 999999 to get 999.99K
    if (absAmount === 999999) {
      return `${sign}999.99 KCr`;
    }
    return `${sign}${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)} KCr`;
  } else {
    return `${sign}${absAmount.toLocaleString()} Cr`;
  }
};

/**
 * Format a number as full currency in credits (no MCr conversion)
 * @param amount - The amount to format
 * @returns Formatted currency string in full credits
 */
export const formatFullCurrency = (amount: number | null | undefined): string => {
  if (amount == null || isNaN(amount)) {
    return "0 Cr";
  }
  return `${amount.toLocaleString()} Cr`;
};

/**
 * Format a number with proper formatting
 * @param value - The number to format
 * @returns Formatted number string
 *
 * @example
 * ```typescript
 * formatNumber(100.5); // "100.5"
 * formatNumber(100); // "100"
 * formatNumber(1000); // "1,000"
 * formatNumber(undefined); // "0"
 * formatNumber(null); // "0"
 * ```
 */
export const formatNumber = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) {
    return "0";
  }
  
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }
  return value.toLocaleString();
};

/**
 * Format a number as a weight value in tons
 * @param value - The number to format
 * @returns Formatted weight string
 *
 * @example
 * ```typescript
 * formatWeight(100.52); // "100.52 tons"
 * ```
 */
export const formatWeight = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) {
    return "0 tons";
  }
  return value.toFixed(2) + " tons";
};

/**
 * Format a number as a power value
 * @param value - The number to format
 * @returns Formatted power string
 *
 * @example
 * ```typescript
 * formatPower(100); // "100 Power"
 * ```
 */
export const formatPower = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) {
    return "0 Power";
  }
  return value + " Power";
};

/**
 * Format a number as a percentage
 * @param value - The number to format
 * @returns Formatted percentage string
 *
 * @example
 * ```typescript
 * formatPercentage(0.5); // "50%"
 * formatPercentage(0.125); // "12.5%"
 * ```
 */
export const formatPercentage = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) {
    return "0%";
  }
  const percentage = value * 100;
  return `${percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1)}%`;
};

/**
 * Format a number as a rating (e.g., Jump-2, Maneuver-3)
 * @param value - The number to format
 * @param type - The type of rating
 * @returns Formatted rating string
 *
 * @example
 * ```typescript
 * formatRating(2, 'Jump'); // "Jump-2"
 * ```
 */
export const formatRating = (value: number | null | undefined, type: string): string => {
  if (value == null || isNaN(value)) {
    return `${type}-0`;
  }
  return `${type}-${value}`;
};

/**
 * Format a date in the Traveller universe format
 * @param date - The date to format
 * @returns Formatted date string
 *
 * @example
 * ```typescript
 * formatDate(new Date()); // "1105-123"
 * ```
 */
export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const day = Math.floor(
    (date.getTime() - new Date(year, 0, 0).getTime()) / (1000 * 60 * 60 * 24),
  );
  return `${year}-${day.toString().padStart(3, "0")}`;
};

/**
 * Format a ship's registry number
 * @param registry - The registry number to format
 * @returns Formatted registry string
 *
 * @example
 * ```typescript
 * formatRegistry('12345'); // "TAS-12345"
 * ```
 */
export const formatRegistry = (registry: string): string => {
  return `TAS-${registry}`;
};

/**
 * Format a ship's tech level
 * @param level - The tech level to format
 * @returns Formatted tech level string
 *
 * @example
 * ```typescript
 * formatTechLevel(10); // "TL-10"
 * ```
 */
export const formatTechLevel = (level: number | null | undefined): string => {
  if (level == null || isNaN(level)) {
    return "TL-0";
  }
  return `TL-${level}`;
};

/**
 * Format a ship's size class
 * @param size - The ship size in tons
 * @returns Formatted size class string
 *
 * @example
 * ```typescript
 * formatSizeClass(1000); // "1,000 ton"
 * ```
 */
export const formatSizeClass = (size: number | null | undefined): string => {
  if (size == null || isNaN(size)) {
    return "0 ton";
  }
  return `${new Intl.NumberFormat("en-US").format(size)} ton`;
};

/**
 * Format a number as a weight value in tons (alias for formatWeight)
 * @param value - The number to format
 * @returns Formatted weight string
 *
 * @example
 * ```typescript
 * formatTons(100.5); // "100.5 tons"
 * ```
 */
export const formatTons = formatWeight;

/**
 * Format a number as tonnage with proper formatting
 * @param value - The number to format
 * @returns Formatted tonnage string
 *
 * @example
 * ```typescript
 * formatTonnage(100.52); // "100.52 tons"
 * formatTonnage(1000.75); // "1,000.75 tons"
 * ```
 */
export const formatTonnage = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) {
    return "0.00 tons";
  }
  // For numbers with decimals, use toFixed(2) to ensure 2 decimal places
  // For whole numbers, still show .00 for consistency
  return `${value.toFixed(2)} tons`;
};
